/**
 * The store page's "Left to do before the client signs in" card (BLU-1376).
 *
 * One list, derived from data the page already loads (`platform.getOrganization`
 * and `platform.getShopifyConnection`), so it survives a reload and shrinks
 * as each item is done:
 *  - register the store's Shopify app (no custom app and no other connection);
 *  - paste the install link (an app with no link that is not connected yet);
 *  - invite the store's admin (no Admin member and no pending Admin invite).
 *
 * When Create Store stopped part way, the page arrives with the failure in
 * router state (`readCreateStoreFailure`), and the card shows the reason in
 * amber above the list. Shown only for an org still in setup.
 *
 * Platform pages are English only, like the rest of the platform tools.
 */
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  saveErrorText,
  type PlatformConnection,
} from '@/features/organization/components/shopify-connection/presentation';

export type CreateStoreFailedStep = 'shopify' | 'installLink' | 'invite';

export interface CreateStoreFailure {
  failedStep: CreateStoreFailedStep;
  code: string;
  detail: string | null;
}

const FAILED_STEPS: ReadonlySet<string> = new Set<CreateStoreFailedStep>(['shopify', 'installLink', 'invite']);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** Strict guard for the router state Create Store sets on a partial result; anything else is null. */
export function readCreateStoreFailure(state: unknown): CreateStoreFailure | null {
  if (!isRecord(state)) return null;
  const failure = state.createStoreFailure;
  if (!isRecord(failure)) return null;
  const { failedStep, code, detail } = failure;
  if (typeof failedStep !== 'string' || !FAILED_STEPS.has(failedStep)) return null;
  if (typeof code !== 'string') return null;
  if (detail !== null && typeof detail !== 'string') return null;
  return { failedStep: failedStep as CreateStoreFailedStep, code, detail };
}

export interface StoreLeftToDoProps {
  setupRequired: boolean;
  setupCompletedAt: string | null;
  /** `platform.getShopifyConnection` data; undefined while it loads. */
  connection: PlatformConnection | undefined;
  members: ReadonlyArray<{ role: string }>;
  hasPendingAdminInvite: boolean;
  failure: CreateStoreFailure | null;
}

/** A sentence ending in exactly one full stop. */
const sentence = (text: string): string => `${text.replace(/\.+$/, '')}.`;

function useFailureText(failure: CreateStoreFailure | null): string | null {
  const { t } = useTranslation('settings');
  if (!failure) return null;
  switch (failure.failedStep) {
    case 'shopify': {
      const reason = failure.code === 'internal' ? 'something went wrong' : saveErrorText(t, failure.code);
      return sentence(`The Shopify app was not registered: ${reason}`);
    }
    case 'installLink':
      return 'The install link was not saved.';
    case 'invite':
      return failure.detail ? sentence(`The admin invite was not sent: ${failure.detail}`) : 'The admin invite was not sent.';
  }
}

export function StoreLeftToDo({
  setupRequired,
  setupCompletedAt,
  connection,
  members,
  hasPendingAdminInvite,
  failure,
}: StoreLeftToDoProps) {
  const failureText = useFailureText(failure);
  if (!setupRequired || setupCompletedAt || !connection) return null;

  const items: string[] = [];
  if (!connection.customApp && connection.mode === 'none') items.push("Register the store's Shopify app");
  if (connection.customApp && !connection.customApp.installLink && connection.customApp.status !== 'connected') {
    items.push('Paste the install link');
  }
  if (!members.some((member) => member.role === 'Admin') && !hasPendingAdminInvite) {
    items.push("Invite the store's admin");
  }

  if (items.length === 0 && !failureText) return null;

  return (
    <Card className="mb-6">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">Left to do before the client signs in</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {failureText && (
          <p role="alert" className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">
            {failureText}
          </p>
        )}
        {items.length > 0 && (
          <ul className="list-disc space-y-1 pl-5 text-sm">
            {items.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
